import React, {useMemo} from 'react';
import * as THREE from 'three';
import { degToRad, RAD2DEG, radToDeg } from 'three/src/math/MathUtils.js';

import { Html, Line,  } from '@react-three/drei';
import { processRollAverage, processRollAverageKalman } from './RollFilter.ts';

interface BasisVectors {
  up: number[];
  right: number[];
  forward: number[];
}

export interface FrameColmap {
  frame: string;
  translation_threejs: number[];
  translation_colmap: number[];
  quaternion_threejs: number[];
  quaternion_colmap: number[];
  basis_threejs: BasisVectors;
  basis_colmap: BasisVectors;
}

export interface Frame {
  frame: string;
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
  euler?: THREE.Euler;
  forward? : THREE.Vector3;

  center2d?: THREE.Vector2;
  height?: number;
  rollaverage?: number;
}

function FrameColmapToFrame(frame: FrameColmap): Frame {
  const position = new THREE.Vector3(...frame.translation_threejs);

  //NOTE: I think I need to use the input quaternions instead?
  //const quaternion = new THREE.Quaternion(...frame.quaternion_threejs);
  const quaternion = basisToQuaternion(frame.basis_threejs);
  return {
    frame: frame.frame,
    position,
    quaternion,
  };
}

export function ProcessFrames(framesinput: FrameColmap[], frame0posout : THREE.Vector3, frame0quatout: THREE.Quaternion): Frame[] {
  let frames = framesinput.map(FrameColmapToFrame);
  let frame0 = frames[0];
  //let frame0position = frame0.position.clone();
  let frame0Quaternion = frame0.quaternion.clone();
  let frame0position = new THREE.Vector3(0, 0, 0);
  //let frame0Quaternion = new THREE.Quaternion(0, 0, 0, 1);

  frame0posout.copy(frame0position);
  frame0quatout.copy(frame0Quaternion);
  
  let framesnormalized = frames.map(frame => NormalizeFrame(frame0position, frame0Quaternion, frame));
  
  // compute euler angles
  framesnormalized.forEach((frame, index) => {
    frame.euler = ComputeEulerAngles(frame.quaternion);

    console.log("yaw", radToDeg(frame.euler.y));
    console.log("pitch", radToDeg(frame.euler.x));
    console.log("roll", radToDeg(frame.euler.z));
  });

  //todo: compute rollaverage
  processRollAverage(framesnormalized, 20);
  //processRollAverageKalman(framesnormalized);

  // compute forward vector
  framesnormalized.forEach((frame, index) => {
    frame.forward = new THREE.Vector3(0, 0, -1).applyQuaternion(frame.quaternion);
  });

  //TODO: I need to call a function to find the focal point of the frames
  //      this function needs to compare the first frame ray with the rest of the frame rays
  //      and output the average (nearest distance between the rays)
  let focalPoint = computeFocalpoint(framesnormalized);

  //todo: given the list of normalized frames, and the focalPoint (center)
  //      need to compute the location of the center in screen space
  //      here are the camera parameters
  let camera = {x: 1920, y: 1080, fx: 1732.5442230159736, fy: 1734.2743122865668, cx: 960, cy: 540};
  
  framesnormalized.forEach((frame, index) => {
    frame.center2d = projectPoint(focalPoint, camera, frame);

    console.log("frame.center2d", frame.center2d);
  });

  

 
  //todo: compute the radius
  const radius = 1.4;

  framesnormalized.forEach((frame, index) => {
    const height = projectSphere(focalPoint, radius, camera, frame);

    const height2 = projectSpherePoints(focalPoint, radius, camera, frame);

    frame.height = height2;

    console.log("frame.height", frame.height);
    console.log("difference", frame.height - height2);


  });

  //
  // try to apply rotation correction
  //

  /* untested
  let frame90 = findNearestFrame(framesnormalized, degToRad(90), 0);
  let frame180 = findNearestFrame(framesnormalized, degToRad(180), 0);

  let to180 = frame180.position.clone().sub(frame0.position);
  
  // Calculate nearest point on line
  let to180Direction = to180.clone().normalize();
  let frame90ToOrigin = frame90.position.clone();
  let projection = to180Direction.multiplyScalar(frame90ToOrigin.dot(to180Direction));
  
  // Vector from line to frame90 point
  let forward = to180Direction;
  let right = frame90.position.clone().sub(projection).normalize();

  let correction = ForwardRightToQuaternion(forward,right);

  let framesrotated = framesnormalized.map(frame => RotateFrame(correction, frame));

  //return framesrotated;
  //note: the corrected rotation is not correct.
  //note: this will make things lopsided because I tilted as a went.
  */

  return framesnormalized;
}

export function findNearestFrame(frames: Frame[], yaw: number, pitch: number): Frame {
  let nearestFrame = frames[0];
  let nearestDistance = Infinity;
  for (let i = 0; i < frames.length; i++) {
    const frame = frames[i];
    const euler = frame.euler!;
    const yawDistance = Math.abs(euler.y - yaw)*1;
    const pitchDistance = Math.abs(euler.x - pitch);
    const distance = yawDistance + pitchDistance;
    if (distance < nearestDistance) {
      nearestFrame = frame;
      nearestDistance = distance;
    }
  }

  return nearestFrame;

}


function NormalizeFrame(frame0Position: THREE.Vector3, frame0Quaternion: THREE.Quaternion, frame: Frame): Frame {

  const inverseFrame0Quaternion = frame0Quaternion.clone().invert();
  const conjugateFrame0Quaternion = frame0Quaternion.clone().invert();

  return {
    frame: frame.frame,
    position: frame.position.clone().sub(frame0Position).applyQuaternion(conjugateFrame0Quaternion),
    quaternion: frame.quaternion.clone().premultiply(conjugateFrame0Quaternion),
  };
}

function RotateFrame(quaternion: THREE.Quaternion, frame: Frame): Frame {
  return {
    frame: frame.frame,
    position: frame.position.clone().applyQuaternion(quaternion),
    quaternion: frame.quaternion.clone().premultiply(quaternion),
    euler: frame.euler,
  };
}

function ComputeEulerAngles(quaternion: THREE.Quaternion): THREE.Euler {
  const euler = new THREE.Euler(0,0,0, "YXZ");
  euler.setFromQuaternion(quaternion, "YXZ");
  return euler;
}

function projectPoint(worldPoint: THREE.Vector3, camera: {x: number, y: number, fx: number, fy: number, cx: number, cy: number}, frame: Frame): THREE.Vector2 {
  // Transform world point to camera space
  const cameraSpacePoint = worldPoint.clone()
      .sub(frame.position)
      .applyQuaternion(frame.quaternion.clone().invert());

  // Project to screen space
  const x = (-cameraSpacePoint.x * camera.fx) / cameraSpacePoint.z + camera.cx;
  const y = (-cameraSpacePoint.y * camera.fy) / cameraSpacePoint.z + camera.cy;
  
  console.log("cameraSpacePoint",cameraSpacePoint);

  return new THREE.Vector2(x, y);
}



interface OutputVisualizeProps {
  frames: FrameColmap[];
  offset?: [number, number, number];
}

export function OutputVisualizeColmap({ frames, offset = [0, 0, 0] }: OutputVisualizeProps) {
      const { processedframes, frame0, frame90, frame180, to180, to180Direction, frame90ToOrigin, projection, dforward, dright, focalPoint, radius } = useMemo(() => {
        let adjustpos = new THREE.Vector3();
        let adjustquat = new THREE.Quaternion();
        const processedframes = ProcessFrames(frames, adjustpos, adjustquat);
        const frame0 = processedframes[0];

        const frame90 = findNearestFrame(processedframes, degToRad(90), 0);
        const frame180 = findNearestFrame(processedframes, degToRad(180), 0);
        const to180 = frame180.position.clone().sub(frame0.position);
  
        const to180Direction = to180.clone().normalize();
        const frame90ToOrigin = frame90.position.clone();
        const projection = to180Direction.multiplyScalar(frame90ToOrigin.dot(to180Direction));
  
        const dforward = to180Direction;
        const dright = frame90.position.clone().sub(projection);

        const focalPoint = computeFocalpoint(processedframes);
        const radius = computeRadius(focalPoint, processedframes);

        return {
          processedframes,
          frame0,
          frame90,
          frame180,
          to180,
          to180Direction,
          frame90ToOrigin,
          projection,
          dforward,
          dright,
          focalPoint,
          radius
        };
      }, [frames]);
  //1920 1080 1739.3463746950431 1737.6085233085294 960 540
  let camera = {x: 1920, y: 1080, fx: 1739.3463746950431, fy: 1737.6085233085294, cx: 960, cy: 540};

  //todo: calculate this from cameras
  let focusradius = 1.4;

      const renderdebug = false;

    return (
        <group
    rotation={new THREE.Euler(degToRad(0), degToRad(0), degToRad(0))}
    
        >
    <group
    
    
    position={
       focalPoint.clone().negate()
    
          }
    >

          
     {/* debug information */}
     <mesh
                position={focalPoint}
                renderOrder={-10}
              >
                <sphereGeometry args={[0.5]} />
                <meshStandardMaterial 
                  color={new THREE.Color("orange")}
                  side={THREE.BackSide}
                  transparent
                  opacity={0.1}
                  depthTest={false}
                  
                />
                </mesh>
                <mesh
                position={focalPoint}
                renderOrder={-10}
              >
                <sphereGeometry args={[focusradius]} />
                <meshStandardMaterial 
                  color={new THREE.Color("green")}
                  side={THREE.BackSide}
                  transparent
                  opacity={0.1}
                  depthTest={false}
                />
                </mesh>

                <mesh
                position={focalPoint}
              >
                <sphereGeometry args={[radius]} />
                <meshStandardMaterial 
                  color={new THREE.Color("lightblue")}
                  side={THREE.BackSide}
                  transparent
                  opacity={0.1}
                  depthTest={false}
                  
                />
                </mesh>

{/* test: 180, 90 alignment check
       <Line
              points={[frame0.position, to180]}                      
              color={"purple"}       // Default
              lineWidth={2}           // In pixels (default)
              // segments             // If true, renders a THREE.LineSegments2. Otherwise, renders a THREE.Line2
            />
    
    <Line
              points={[projection, frame90.position]}                      
              color={"orange"}       // Default
              lineWidth={2}           // In pixels (default)
              // segments             // If true, renders a THREE.LineSegments2. Otherwise, renders a THREE.Line2
            />
*/}

      {processedframes.map((frame, index) => {

        const position = frame.position;
        const rotation = frame.quaternion;
        const up = new THREE.Vector3(0, 1, 0).applyQuaternion(rotation);
        const right = new THREE.Vector3(1, 0, 0).applyQuaternion(rotation);
        const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(rotation);

        const linescale = 1;

        const scale = frame.frame == "frame-135.png" ? 1: 1;

        const posup = position.clone().add(up.multiplyScalar(linescale));
        const posright = position.clone().add(right.multiplyScalar(linescale));
        const posforward = position.clone().add(forward.multiplyScalar(linescale));

        


        return (
          <group key={frame.frame}
            
          
          >
       

        {renderdebug &&
        <>
            <Line
              points={[position, posforward]}                      
              color={"green"}       // Default
              lineWidth={1}           // In pixels (default)
              // segments             // If true, renders a THREE.LineSegments2. Otherwise, renders a THREE.Line2
            />
           
               <Line
              points={[position, posup]}                      
              color={"blue"}       // Default
              lineWidth={1}           // In pixels (default)
              // segments             // If true, renders a THREE.LineSegments2. Otherwise, renders a THREE.Line2
            /> 
            <Line
              points={[position, posright]}                      
              color={"red"}       // Default
              lineWidth={1}           // In pixels (default)
              // segments             // If true, renders a THREE.LineSegments2. Otherwise, renders a THREE.Line2
            />
            {/*
            */}
              <mesh
                position={position}
                quaternion={rotation}
                onPointerOver={(e) => e.object.scale.set(3, 3, 3)}
                onPointerOut={(e) => e.object.scale.set(1, 1, 1)}
              >
                <boxGeometry args={[0.1 * scale, 0.2 * scale, 0.025 * scale]} />
                <meshStandardMaterial 
                  color={new THREE.Color(`hsl(${(index / frames.length) * 300}, 100%, 50%)`)}
                  side={THREE.DoubleSide}
                  transparent
                  opacity={0.7}
                />
                {/* debug 
                <Html
                  style={{
                    backgroundColor: 'rgba(0,0,0,0.8)',
                    padding: '8px',
                    color: 'white',
                    borderRadius: '4px',
                    fontSize: '2px',
                  }}
                  occlude
                  distanceFactor={6}
                >
                  {`Yaw: ${(frame.euler!.y * (180/Math.PI)).toFixed(2)}°\nPitch: ${(frame.euler!.x * (180/Math.PI)).toFixed(2)}°\nRoll: ${(frame.euler!.z * (180/Math.PI)).toFixed(2)}°`}
                </Html>
                 */}
              </mesh>
              {/*
            <mesh
              position={position}
              quaternion={basisquat}
            >
              <boxGeometry args={[0.02, 0.4, 0.02]} />
              <meshStandardMaterial 
                color={new THREE.Color(`hsl(${(index / frames.length) * 300}, 100%, 50%)`)}
                side={THREE.DoubleSide}
                transparent
                opacity={0.7}
              />
            </mesh>
            
            <mesh
              position={[
                transX + offset[0],
                transY + offset[1],
                transZ + offset[2]
              ]}
              quaternion={new THREE.Quaternion(qx, qy, qz, qw)}
            >
              <boxGeometry args={[0.5, 0.75, 0.25]} translate={[0, 0, 1]} />
              <meshStandardMaterial 
                color={new THREE.Color(`hsl(${(index / frames.length) * 300}, 100%, 50%)`)}
                side={THREE.DoubleSide}
                transparent
                opacity={0.7}
              />
            </mesh>
            */}
                </>
          }

          </group>
        );
      })}
    </group>
    </group>
  );
}

function basisToQuaternion(basis: BasisVectors): THREE.Quaternion {
    const rotationMatrix = new THREE.Matrix4();
    const right = new THREE.Vector3(...basis.right);
    const up = new THREE.Vector3(...basis.up);
    const forward = new THREE.Vector3(...basis.forward);
    
    rotationMatrix.makeBasis(right, up, forward.negate());
    
    const quaternion = new THREE.Quaternion();
    quaternion.setFromRotationMatrix(rotationMatrix);
    
    return quaternion;
}

//note: the forward and right vectors are perpendicular
function ForwardRightToQuaternion(forward: THREE.Vector3, right: THREE.Vector3) : THREE.Quaternion
{
    const rotationMatrix = new THREE.Matrix4();
    const up = forward.clone().cross(right).normalize();

    rotationMatrix.makeBasis(right, up, forward);

    const quaternion = new THREE.Quaternion();
    quaternion.setFromRotationMatrix(rotationMatrix);

    return quaternion;
}

function nearestPointRayRay(
  origin1: THREE.Vector3, 
  direction1: THREE.Vector3,
  origin2: THREE.Vector3, 
  direction2: THREE.Vector3
): THREE.Vector3 {
  const n = direction1.clone().cross(direction2);
  const n1 = direction1.clone().cross(n);
  const n2 = direction2.clone().cross(n);

  const c1 = origin1.clone().add(direction1.clone().multiplyScalar(
    origin2.clone().sub(origin1).dot(n2) / direction1.dot(n2)
  ));

  const c2 = origin2.clone().add(direction2.clone().multiplyScalar(
    origin1.clone().sub(origin2).dot(n1) / direction2.dot(n1)
  ));

  return c1.clone().add(c2).multiplyScalar(0.5);
}

export function computeFocalpoint(frames: Frame[]): THREE.Vector3 {
  const frame0 = frames[0];
  let totalIntersectionPoint = new THREE.Vector3();
  let intersectionCount = 0;

  for (let i = 1; i < frames.length; i++) {
    const frame = frames[i];
    const intersectionPoint = nearestPointRayRay(
      frame0.position,
      frame0.forward!,
      frame.position,
      frame.forward!
    );
    totalIntersectionPoint.add(intersectionPoint);
    intersectionCount++;
  }

  return totalIntersectionPoint.divideScalar(intersectionCount);
}

function computeRadius(centerPoint: THREE.Vector3, frames: Frame[]): number {
  let maxDistance = 0;
  
  frames.forEach(frame => {
    const distance = frame.position.distanceTo(centerPoint);
    maxDistance = Math.max(maxDistance, distance);
  });

  return maxDistance;
}

function projectSphere(
  worldCenter: THREE.Vector3,
  r: number,
  camera: { x: number; y: number; fx: number; fy: number; cx: number; cy: number },
  frame: Frame
): number {
  // Convert world center to camera space using the inverse of the frame's rotation.
  const camSpaceCenter = worldCenter.clone()
    .sub(frame.position)
    .applyQuaternion(frame.quaternion.clone().invert());

  // If the sphere is behind the camera, it cannot be projected.
  if (camSpaceCenter.z <= 0) {
    return 0;
  }

  // The distance from the camera to the sphere center.
  const d = camSpaceCenter.length();

  // Compute the tangent angle (theta) from the camera center to the sphere.
  // Clamp r/d to a maximum of 1 to avoid numerical errors.
  const theta = Math.asin(Math.min(1, r / d));

  // Using the vertical focal length fy, the projected radius is:
  const projRadius = camera.fy * Math.tan(theta);

  // The sphere’s projected height in pixels is twice the projected radius.
  return 2 * projRadius;
}


function projectSpherePoints(
  worldCenter: THREE.Vector3,
  r: number,
  camera: { x: number; y: number; fx: number; fy: number; cx: number; cy: number },
  frame: Frame
): number {
  // Convert the sphere's center to camera space.
  const P = worldCenter.clone().sub(frame.position).applyQuaternion(frame.quaternion.clone().invert());
  const d = P.length();
  if (d < r) {
    // Avoid problems when the sphere engulfs the camera.
    return camera.y;
  }
  // Compute the tangent angle at the sphere: theta = arcsin(r / d)
  const theta = Math.asin(Math.min(1, r / d));
  const pNorm = P.clone().normalize();
  
  // In camera space, the up vector is (0, 1, 0). Compute an axis perpendicular to both pNorm and up.
  let upCam = new THREE.Vector3(0, 1, 0);
  let rAxis = new THREE.Vector3().crossVectors(pNorm, upCam).normalize();
  if (rAxis.lengthSq() < 1e-6) {
    // If the view direction is collinear with the up vector, choose an alternative axis.
    rAxis = new THREE.Vector3(1, 0, 0);
  }
  // Compute a vector in the plane defined by pNorm and up such that it is orthogonal to pNorm.
  const v = new THREE.Vector3().crossVectors(rAxis, pNorm).normalize();
  
  const cosTheta = Math.cos(theta);
  const sinTheta = Math.sin(theta);
  
  // The tangent directions corresponding to the top and bottom tangent rays.
  // They are computed by rotating pNorm by +theta and -theta about rAxis.
  const topDir = pNorm.clone().multiplyScalar(cosTheta).add(v.clone().multiplyScalar(sinTheta));
  const bottomDir = pNorm.clone().multiplyScalar(cosTheta).sub(v.clone().multiplyScalar(sinTheta));
  
  // Pick an arbitrary distance L along the ray.
  const L = 1;
  const topCameraPoint = topDir.multiplyScalar(L);
  const bottomCameraPoint = bottomDir.multiplyScalar(L);
  
  // Convert these camera-space points back to world space:
  const worldTop = topCameraPoint.clone().applyQuaternion(frame.quaternion).add(frame.position);
  const worldBottom = bottomCameraPoint.clone().applyQuaternion(frame.quaternion).add(frame.position);
  
  // Use the provided projectPoint function to get screen coordinates.
  const projTop = projectPoint(worldTop, camera, frame);
  const projBottom = projectPoint(worldBottom, camera, frame);
  
  // The sphere's projected height is the vertical (y) pixel difference.
  return Math.abs(projTop.y - projBottom.y);
}